import { apiClient } from './client'
import type { ApiResponse } from './client'
import type { AudienceSearchResultDto, AudienceSearchQuery, CampaignFileImportListDto } from '@/types/audience'
import type { CampaignFileImportCreateDto } from '@/types/campaignFileImport'
import type { CampaignListAnalyticsResponseDto } from '@/types/campaignAnalytics'
import type { PhoneEnrichmentResultDto } from './leads'

export const audienceApi = {
    search: async (
        clientCompanyId: string,
        workspaceId: string,
        filters?: AudienceSearchQuery
    ): Promise<ApiResponse<AudienceSearchResultDto[]>> => {
        const params = new URLSearchParams()
        if (filters?.CampaignId) params.append('campaignId', filters.CampaignId)
        if (filters?.CampaignStatuses) filters.CampaignStatuses.forEach(s => params.append('campaignStatus', s))
        if (filters?.CampaignName) params.append('campaignName', filters.CampaignName)
        if (filters?.CreatedDateFrom) params.append('createdDateFrom', filters.CreatedDateFrom)
        if (filters?.CreatedDateTo) params.append('createdDateTo', filters.CreatedDateTo)
        const qs = params.toString()
        return apiClient.get(`/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Audiences${qs ? '?' + qs : ''}`)
    },

    // Create a new audience with file upload
    create: async (clientCompanyId: string, workspaceId: string, data: CampaignFileImportCreateDto, file: File): Promise<ApiResponse<AudienceSearchResultDto>> => {
        const formData = new FormData()
        formData.append('data', JSON.stringify(data))
        formData.append('file', file)
        return apiClient.post(`/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Audiences`, formData)
    },

    // Get analytics for a specific audience
    getAnalytics: async (clientCompanyId: string, workspaceId: string, campaignId: string, audienceId: string, signal?: AbortSignal): Promise<ApiResponse<CampaignListAnalyticsResponseDto>> => {
        return apiClient.get(`/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Campaigns/${campaignId}/Audiences/${audienceId}/analytics`, { signal })
    },

    // List audiences by campaign
    listByCampaign: (clientCompanyId: string, workspaceId: string, campaignId: string): Promise<ApiResponse<CampaignFileImportListDto[]>> =>
        apiClient.get(`/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Campaigns/${campaignId}/Audiences`),

    // Phone enrichment for file import rows
    enrichPhone: (clientCompanyId: string, workspaceId: string, campaignId: string, fileImportRowId: string, signal?: AbortSignal): Promise<ApiResponse<PhoneEnrichmentResultDto>> => {
        const url = `/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Campaigns/${campaignId}/Audiences/${fileImportRowId}/EnrichPhone`
        return apiClient.post(url, {}, { signal })
    },

    syncPhone: (clientCompanyId: string, workspaceId: string, campaignId: string, fileImportRowId: string, signal?: AbortSignal): Promise<ApiResponse<PhoneEnrichmentResultDto>> => {
        const url = `/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Campaigns/${campaignId}/Audiences/${fileImportRowId}/SyncPhone`
        return apiClient.post(url, {}, { signal })    },

    // Re-enrich file import row (full contact data enrichment)
    reEnrich: (clientCompanyId: string, workspaceId: string, campaignId: string, fileImportRowId: string, signal?: AbortSignal): Promise<ApiResponse<void>> => {
        const url = `/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Campaigns/${campaignId}/Audiences/${fileImportRowId}/re-enrich`
        return apiClient.post(url, {}, { signal })
    }
}

export default audienceApi
